const {overlapDays, weekState} = require('../../../shared/workforcePlanWeek');
const {focusTarget} = require('./employeeWorkforcePlanNavigation');

function dateValue(value) {
    return new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
}

function isoDate(value) {
    return value.toISOString().slice(0, 10);
}

function addDays(value, days) {
    const result = dateValue(value);
    result.setUTCDate(result.getUTCDate() + Number(days || 0));
    return isoDate(result);
}

function mondayOnOrBefore(value) {
    const result = dateValue(value);
    const offset = (result.getUTCDay() + 6) % 7;
    result.setUTCDate(result.getUTCDate() - offset);
    return isoDate(result);
}

function isoWeekNumber(value) {
    const result = dateValue(value);
    const day = result.getUTCDay() || 7;
    result.setUTCDate(result.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(result.getUTCFullYear(), 0, 1));
    return Math.ceil((((result - yearStart) / 86400000) + 1) / 7);
}

function yearWindow(year) {
    const normalizedYear = Number(year);
    const from = `${normalizedYear}-01-01`;
    const to = `${normalizedYear + 1}-01-01`;
    const weeks = [];
    let startsOn = mondayOnOrBefore(from);
    while (startsOn < to) {
        const endsOn = addDays(startsOn, 7);
        weeks.push({
            key: startsOn,
            starts_on: startsOn,
            ends_on: endsOn,
            visible_starts_on: startsOn < from ? from : startsOn,
            visible_ends_on: endsOn > to ? to : endsOn
        });
        startsOn = endsOn;
    }
    return {from, to, weeks};
}

function rollingWindow(date) {
    const from = addDays(mondayOnOrBefore(date), -26 * 7);
    const weeks = Array.from({length: 53}, function (_, index) {
        const startsOn = addDays(from, index * 7);
        const endsOn = addDays(startsOn, 7);
        return {
            key: startsOn,
            starts_on: startsOn,
            ends_on: endsOn,
            visible_starts_on: startsOn,
            visible_ends_on: endsOn
        };
    });
    return {from, to: addDays(from, 53 * 7), weeks};
}

function planViewModeKey(user) {
    const value = user || {};
    const userId = value.userId || value.user_id;
    return value.company && userId
        ? `employee-workforce:plan-view-mode:v1:${value.company}:${userId}`
        : null;
}

function readPlanViewMode(user, storage) {
    try {
        const key = planViewModeKey(user);
        const target = storage || (typeof window !== 'undefined' && window.localStorage);
        return key && target && target.getItem(key) === 'year' ? 'year' : 'rolling';
    } catch (error) {
        return 'rolling';
    }
}

function writePlanViewMode(user, mode, storage) {
    try {
        const key = planViewModeKey(user);
        const target = storage || (typeof window !== 'undefined' && window.localStorage);
        if (key && target) target.setItem(key, mode === 'year' ? 'year' : 'rolling');
    } catch (error) {
        // A device preference must not prevent opening the plan.
    }
}

function buildWeekStateIndex(employees, weeks) {
    const result = new Map();
    const weekItems = (Array.isArray(weeks) ? weeks : []).map(function (week, index) {
        return {week, index};
    });
    const compareWeeks = function (left, right) {
        return String(left.week.visible_starts_on).localeCompare(
            String(right.week.visible_starts_on)
        ) || String(left.week.visible_ends_on).localeCompare(
            String(right.week.visible_ends_on)
        ) || left.index - right.index;
    };
    const weeksAreOrdered = weekItems.every(function (item, index) {
        return index === 0 || compareWeeks(weekItems[index - 1], item) <= 0;
    });
    const orderedWeeks = weeksAreOrdered ? weekItems : weekItems.slice().sort(compareWeeks);
    (Array.isArray(employees) ? employees : []).forEach(function (employee) {
        const states = new Map();
        const employeeIntervals = (Array.isArray(employee.intervals) ? employee.intervals : [])
            .filter(function (interval) {
                return interval && interval.starts_on && interval.ends_on;
            });
        const compareIntervals = function (left, right) {
            return String(left.starts_on).localeCompare(
                String(right.starts_on)
            ) || String(left.ends_on).localeCompare(
                String(right.ends_on)
            );
        };
        const intervalsAreOrdered = employeeIntervals.every(function (interval, index) {
            return index === 0
                || compareIntervals(employeeIntervals[index - 1], interval) <= 0;
        });
        const intervals = intervalsAreOrdered
            ? employeeIntervals
            : employeeIntervals.slice().sort(compareIntervals);
        let intervalIndex = 0;
        let activeIntervals = [];
        orderedWeeks.forEach(function (item) {
            const week = item.week;
            while (
                intervalIndex < intervals.length
                && intervals[intervalIndex].starts_on < week.visible_ends_on
            ) {
                activeIntervals.push(intervals[intervalIndex]);
                intervalIndex += 1;
            }
            activeIntervals = activeIntervals.filter(function (interval) {
                return interval.ends_on > week.visible_starts_on;
            });
            states.set(week.key, weekState(activeIntervals, week));
        });
        result.set(Number(employee.employee_id), states);
    });
    return result;
}

function weekStateFromIndex(index, employee, week) {
    const employeeId = Number(
        employee && employee.employee_id !== undefined
            ? employee.employee_id
            : employee
    );
    const weekKey = week && week.key !== undefined ? week.key : week;
    const states = index instanceof Map ? index.get(employeeId) : null;
    if (!(states instanceof Map) || !states.has(weekKey)) return undefined;
    return states.get(weekKey);
}

function stateOnDate(intervals, value) {
    const date = String(value || '').slice(0, 10);
    return (Array.isArray(intervals) ? intervals : []).find(function (interval) {
        return interval.starts_on <= date && interval.ends_on > date;
    }) || null;
}

function emptyHeadcount() {
    return {total: 0, workers: 0, itr: 0};
}

function emptyCounts() {
    return {
        total: 0,
        planning_total: 0,
        object_total: 0,
        objects: {},
        object_groups: {},
        planned_objects: {},
        requires_distribution: 0,
        rest: 0,
        loupe: 0,
        missing: 0,
        breakdown: {
            total: emptyHeadcount(),
            planning_total: emptyHeadcount(),
            object_total: emptyHeadcount(),
            objects: {},
            object_groups: {},
            planned_objects: {},
            requires_distribution: emptyHeadcount(),
            rest: emptyHeadcount(),
            loupe: emptyHeadcount(),
            missing: emptyHeadcount()
        }
    };
}

function incrementHeadcount(headcount, itr) {
    headcount.total += 1;
    if (itr) headcount.itr += 1;
    else headcount.workers += 1;
}

function includesTarget(values, id) {
    const targetId = Number(id);
    return Boolean(targetId) && (Array.isArray(values) ? values : []).some(function (value) {
        return Number(value) === targetId;
    });
}

function employeeIsItrForInterval(employee, interval) {
    if (!interval) return false;
    if (interval.state_type === 'object') {
        return includesTarget(employee && employee.itr_building_ids, interval.building_id);
    }
    if (interval.state_type === 'object_group') {
        return includesTarget(
            employee && employee.itr_object_group_ids,
            interval.object_group_id
        );
    }
    if (interval.state_type === 'planned_object') {
        return Boolean(
            employee && employee.source_engineering
            || employee && Array.isArray(employee.itr_building_ids)
                && employee.itr_building_ids.length
            || employee && Array.isArray(employee.itr_object_group_ids)
                && employee.itr_object_group_ids.length
        );
    }
    return false;
}

function incrementTargetCount(counts, collection, id, itr) {
    const key = String(Number(id));
    counts[collection][key] = Number(counts[collection][key] || 0) + 1;
    if (!counts.breakdown[collection][key]) {
        counts.breakdown[collection][key] = emptyHeadcount();
    }
    incrementHeadcount(counts.breakdown[collection][key], itr);
}

function countEmployee(counts, employee, interval) {
    const itr = employeeIsItrForInterval(employee, interval);
    counts.total += 1;
    incrementHeadcount(counts.breakdown.total, itr);
    if (!interval) {
        counts.missing += 1;
        incrementHeadcount(counts.breakdown.missing, false);
        return;
    }
    if (interval.state_type === 'object' && Number(interval.building_id)) {
        incrementTargetCount(counts, 'objects', interval.building_id, itr);
        counts.object_total += 1;
        incrementHeadcount(counts.breakdown.object_total, itr);
        return;
    }
    if (
        interval.state_type === 'object_group'
        && Number(interval.object_group_id)
    ) {
        incrementTargetCount(
            counts,
            'object_groups',
            interval.object_group_id,
            itr
        );
        counts.object_total += 1;
        incrementHeadcount(counts.breakdown.object_total, itr);
        return;
    }
    if (
        interval.state_type === 'planned_object'
        && Number(interval.planned_object_id)
    ) {
        incrementTargetCount(
            counts,
            'planned_objects',
            interval.planned_object_id,
            itr
        );
        counts.object_total += 1;
        incrementHeadcount(counts.breakdown.object_total, itr);
        return;
    }
    if (Object.prototype.hasOwnProperty.call(counts, interval.state_type)) {
        counts[interval.state_type] += 1;
        incrementHeadcount(counts.breakdown[interval.state_type], false);
        return;
    }
    counts.missing += 1;
    incrementHeadcount(counts.breakdown.missing, false);
}

function countsForEmployees(employees, intervalForEmployee) {
    const counts = emptyCounts();
    (Array.isArray(employees) ? employees : []).forEach(function (employee) {
        countEmployee(counts, employee, intervalForEmployee(employee));
    });
    ['object_total', 'requires_distribution', 'rest', 'missing'].forEach(function (key) {
        counts.planning_total += counts[key];
        ['total', 'workers', 'itr'].forEach(function (part) {
            counts.breakdown.planning_total[part] += counts.breakdown[key][part];
        });
    });
    return counts;
}

function weeklyCounts(employees, weeks, stateForEmployeeWeek) {
    return (Array.isArray(weeks) ? weeks : []).map(function (week) {
        return countsForEmployees(employees, function (employee) {
            const state = typeof stateForEmployeeWeek === 'function'
                ? stateForEmployeeWeek(employee, week)
                : weekState(employee.intervals, week);
            return state && state.interval;
        });
    });
}

function countsOnDate(employees, value, intervalForEmployee) {
    return countsForEmployees(employees, function (employee) {
        return typeof intervalForEmployee === 'function'
            ? intervalForEmployee(employee)
            : stateOnDate(employee.intervals, value);
    });
}

function selectedRange(anchorWeek, targetWeek, window) {
    const first = anchorWeek.starts_on < targetWeek.starts_on
        ? anchorWeek
        : targetWeek;
    const last = anchorWeek.starts_on < targetWeek.starts_on
        ? targetWeek
        : anchorWeek;
    const startsOn = first.starts_on < window.from
        ? window.from
        : first.starts_on;
    const endsOn = last.ends_on > window.to ? window.to : last.ends_on;
    return {
        starts_on: startsOn,
        ends_on: endsOn,
        ends_on_inclusive: addDays(endsOn, -1)
    };
}

function rangesOverlap(leftFrom, leftTo, rightFrom, rightTo) {
    return leftFrom < rightTo && leftTo > rightFrom;
}

function sameIntervalTarget(left, right) {
    if (!left || !right || left.state_type !== right.state_type) return false;
    if (left.state_type === 'object') {
        return Number(left.building_id) === Number(right.building_id);
    }
    if (left.state_type === 'object_group') {
        return Number(left.object_group_id) === Number(right.object_group_id);
    }
    if (left.state_type === 'planned_object') {
        return Number(left.planned_object_id) === Number(right.planned_object_id);
    }
    return true;
}

function preserveIntervalTarget(interval, previousIntervals) {
    if (!interval || interval.target_name) return interval;
    const previous = (Array.isArray(previousIntervals) ? previousIntervals : [])
        .find(item => sameIntervalTarget(item, interval) && item.target_name);
    return previous
        ? Object.assign({}, interval, {
            target_name: previous.target_name,
            target_code: interval.target_code || previous.target_code || null,
            target_color: interval.target_color || previous.target_color || null,
            target_is_planned: interval.target_is_planned === true
                || previous.target_is_planned === true
        })
        : interval;
}

function applyIntervalChanges(intervals, changes) {
    return (Array.isArray(changes) ? changes : []).reduce(function (current, change) {
        const range = change && change.changed_range;
        if (!range || !range.starts_on || !range.ends_on) return current;
        const inserted = (Array.isArray(change.intervals) ? change.intervals : [])
            .map(interval => preserveIntervalTarget(interval, current));
        return current.filter(function (interval) {
            return !rangesOverlap(
                interval.starts_on,
                interval.ends_on,
                range.starts_on,
                range.ends_on
            );
        }).concat(inserted).sort(function (left, right) {
            return String(left.starts_on).localeCompare(String(right.starts_on))
                || String(left.ends_on).localeCompare(String(right.ends_on))
                || Number(left.id || 0) - Number(right.id || 0);
        });
    }, Array.isArray(intervals) ? intervals.slice() : []);
}

function applyPlanBatchResult(employees, result) {
    const updates = new Map((result && Array.isArray(result.employees)
        ? result.employees
        : []).map(function (employee) {
        return [Number(employee.employee_id), employee];
    }));
    let applied = 0;
    const nextEmployees = (Array.isArray(employees) ? employees : []).map(function (employee) {
        const update = updates.get(Number(employee.employee_id));
        if (!update || !Array.isArray(update.changes)) return employee;
        applied += 1;
        return Object.assign({}, employee, {
            revision: Number(update.revision),
            intervals: applyIntervalChanges(employee.intervals, update.changes)
        });
    });
    return {employees: nextEmployees, applied};
}

function hasExplicitRouteDate(query) {
    const source = query || {};
    if (source.plan_intent === 'focus') return Boolean(focusTarget(source));
    return ['edit', 'date'].includes(String(source.plan_intent || ''))
        && /^\d{4}-\d{2}-\d{2}$/.test(String(source.date || ''));
}

function initialRouteDate(query, today) {
    return hasExplicitRouteDate(query) ? String(query.date) : String(today);
}

function virtualRowRange(options) {
    const source = options || {};
    const total = Math.max(0, Number(source.total) || 0);
    if (!total) return {start: 0, end: 0};
    const rowHeight = Math.max(1, Number(source.rowHeight) || 1);
    const overscan = Math.max(0, Math.floor(Number(source.overscan) || 0));
    const viewportHeight = Math.max(0, Number(source.viewportHeight) || 0);
    const contentOffset = Math.max(0, Number(source.contentOffset) || 0);
    const scrollTop = Math.max(0, Number(source.scrollTop) || 0);
    const visibleTop = Math.max(0, scrollTop - contentOffset);
    const visibleBottom = Math.max(0, scrollTop + viewportHeight - contentOffset);
    const firstVisible = Math.min(total - 1, Math.floor(visibleTop / rowHeight));
    const lastVisibleExclusive = Math.min(
        total,
        Math.max(firstVisible + 1, Math.ceil(visibleBottom / rowHeight))
    );
    const requestedWindowSize = Math.floor(Number(source.windowSize) || 0);
    if (requestedWindowSize > 0) {
        const windowSize = Math.min(total, Math.max(
            requestedWindowSize,
            lastVisibleExclusive - firstVisible
        ));
        const maxStart = Math.max(0, total - windowSize);
        let start = Math.max(0, Math.min(
            maxStart,
            firstVisible - overscan
        ));
        if (lastVisibleExclusive > start + windowSize) {
            start = Math.min(maxStart, lastVisibleExclusive - windowSize);
        }
        return {start, end: start + windowSize};
    }
    return {
        start: Math.max(0, firstVisible - overscan),
        end: Math.min(total, lastVisibleExclusive + overscan)
    };
}

function currentArrivalConfirmation(employee, interval, date) {
    const fields = {object: 'building_id', object_group: 'object_group_id', planned_object: 'planned_object_id'};
    const field = interval && fields[interval.state_type];
    if (!field || !interval[field] || !date) return null;
    return (employee.arrival_confirmations || []).filter(confirmation => (
        (!confirmation.status || confirmation.status === 'active')
        && confirmation.arrival_date <= date
        && (confirmation.confirmed_presence || []).some(period => (
            period.target_type === interval.state_type
            && Number(period[field]) === Number(interval[field])
            && period.starts_on <= date
            && (!period.ends_on || date < period.ends_on)
        ))
    )).sort((left, right) => String(right.arrival_date).localeCompare(String(left.arrival_date))
        || Number(right.id) - Number(left.id))[0] || null;
}

function baselinePresenceForRange(employee, interval, from, to) {
    if (!interval || !from || !to) return null;
    return (employee.baseline_presence || []).find(period => (
        (interval.state_type === 'object' && Number(interval.building_id) === Number(period.building_id)
            || interval.state_type === 'object_group' && Number(interval.object_group_id)
            && Number(interval.object_group_id) === Number(period.object_group_id))
        && period.starts_on < to && (!period.ends_on || period.ends_on > from)
        && interval.starts_on < to && interval.ends_on > from
        && period.starts_on < interval.ends_on && (!period.ends_on || period.ends_on > interval.starts_on)
    )) || null;
}

module.exports = {
    baselinePresenceForRange,
    rollingWindow,
    readPlanViewMode,
    writePlanViewMode,
    currentArrivalConfirmation,
    addDays,
    applyIntervalChanges,
    applyPlanBatchResult,
    buildWeekStateIndex,
    countsOnDate,
    employeeIsItrForInterval,
    isoWeekNumber,
    hasExplicitRouteDate,
    initialRouteDate,
    mondayOnOrBefore,
    rangesOverlap,
    selectedRange,
    stateOnDate,
    weekState,
    weekStateFromIndex,
    weeklyCounts,
    virtualRowRange,
    yearWindow
};
